import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { VoiceFavoritesService } from '@/services/voice/VoiceFavoritesService';
import { Voice } from '@/types/voice/voice';

const favoritesService = VoiceFavoritesService.getInstance();

// Query keys
export const voiceFavoriteKeys = {
  all: ["voice-favorites"] as const,
  list: () => [...voiceFavoriteKeys.all, "list"] as const,
  detail: (voiceId: string) => [...voiceFavoriteKeys.all, "detail", voiceId] as const,
};

/**
 * Hook to get user's favorite voices
 */
export const useVoiceFavorites = () => {
  return useQuery({
    queryKey: voiceFavoriteKeys.list(),
    queryFn: () => favoritesService.getUserFavorites(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry if table doesn't exist
  });
};

/**
 * Hook to toggle favorite status
 */
export const useToggleFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      voiceId,
      voice,
      isFavorited,
      isSharedVoice,
    }: {
      voiceId: string;
      voice: Voice;
      isFavorited: boolean;
      isSharedVoice?: boolean;
    }) => {
      if (isFavorited) {
        return favoritesService.removeFavorite(voiceId);
      } else {
        // For shared voices (All Voices tab), also call voiceService.addVoice
        if (isSharedVoice) {
          const voiceServiceInstance = await import("@/services/integrations/elevenlabs/VoiceService").then((m) =>
            m.VoiceService.getInstance(),
          );
          try {
            await voiceServiceInstance.addVoice(voice);
          } catch (error) {
            console.error("Failed to add voice to ElevenLabs library:", error);
            throw error;
          }
        }
        return favoritesService.addFavorite(voiceId, voice);
      }
    },
    onSuccess: (_, { voiceId, isFavorited }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: voiceFavoriteKeys.list() });
      queryClient.invalidateQueries({ queryKey: voiceFavoriteKeys.detail(voiceId) });

      // Also invalidate recommended voices queries since they may be affected
      queryClient.invalidateQueries({ queryKey: ["voices", "list"] });

      // Show success message
      if (isFavorited) {
        toast.success("Removed from favorites");
      } else {
        toast.success("Added to favorites");
      }
    },
    onError: (error) => {
      console.error("Failed to toggle favorite:", error);
      toast.error("Failed to update favorites");
    },
  });
};

